if (!customElements.get('holiday-flip-grid')) {
  customElements.define('holiday-flip-grid', class HolidayFlipGrid extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.cards = Array.from(this.querySelectorAll('[data-grid-card]'));
      this.track = this.querySelector('[data-grid-scroll-track]');
      this.boardTrack = this.querySelector('.holiday-grid__board-track');
      this.copy = this.querySelector('.holiday-grid__copy');
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.progress = null;

      this.addEventListener('click', (event) => this.onCardClick(event), { signal });
      if (!this.track || !this.boardTrack || !this.cards.length) return;

      window.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true, signal });
      window.addEventListener('resize', () => this.scheduleMeasure(), { passive: true, signal });
      this.motionPreference.addEventListener('change', () => this.scheduleMeasure(), { signal });

      if (typeof ResizeObserver === 'function') {
        this.observer = new ResizeObserver((entries) => {
          const changed = entries.some((entry) => {
            if (entry.target === this.boardTrack) return Math.abs(entry.contentRect.width - this.trackWidth) > 0.5;
            return Math.abs(entry.contentRect.height - this.copyHeight) > 0.5;
          });
          if (changed) this.scheduleMeasure();
        });
        this.observer.observe(this.boardTrack);
        if (this.copy) this.observer.observe(this.copy);
      }

      this.measure();
    }

    onCardClick(event) {
      const card = event.target.closest('[data-grid-card]');
      if (!card || !this.contains(card) || this.classList.contains('is-scroll-animating')) return;
      this.classList.add('has-interacted');
      this.setCardState(card, !card.classList.contains('is-flipped'));
    }

    setCardState(card, flipped) {
      if (card.getAttribute('aria-pressed') === String(flipped)) return;
      card.classList.toggle('is-flipped', flipped);
      card.setAttribute('aria-pressed', String(flipped));
      const action = flipped ? this.dataset.returnLabel : this.dataset.revealLabel;
      const description = flipped && card.dataset.backDescription ? ` — ${card.dataset.backDescription}` : '';
      card.setAttribute('aria-label', `${action} ${card.dataset.gridCard}${description}`);
    }

    scheduleMeasure() {
      if (this.measureFrame) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = null;
        this.measure();
      });
    }

    measure() {
      if (this.motionPreference.matches) {
        this.resetScroll();
        return;
      }

      const headerHeight = document.querySelector('.section-header')?.getBoundingClientRect().height || 0;
      const availableHeight = Math.max(1, window.innerHeight - headerHeight - 32);
      this.trackWidth = this.boardTrack.getBoundingClientRect().width;
      this.copyHeight = this.copy?.getBoundingClientRect().height || 0;
      const stacked = window.innerWidth < 1024;
      const copySpace = stacked ? this.copyHeight + (window.innerWidth < 768 ? 16 : 24) : 0;
      const boardSize = Math.min(this.trackWidth, Math.max(1, availableHeight - copySpace));
      if (stacked && boardSize < Math.min(this.trackWidth, 180)) {
        this.resetScroll();
        return;
      }
      const stageSize = boardSize + copySpace;
      this.pinTop = headerHeight + 16 + (availableHeight - stageSize) / 2;
      this.scrollDistance = Math.max(720, Math.min(1440, window.innerHeight * 1.15));
      const copyOffset = Math.max(0, (boardSize - this.copyHeight) / 2);

      this.style.setProperty('--holiday-grid-board-size', `${boardSize}px`);
      this.style.setProperty('--holiday-grid-stage-size', `${stageSize}px`);
      this.style.setProperty('--holiday-grid-scroll-distance', `${this.scrollDistance}px`);
      this.style.setProperty('--holiday-grid-pin-top', `${this.pinTop}px`);
      this.style.setProperty('--holiday-grid-copy-pin-top', `${this.pinTop + copyOffset}px`);
      this.style.setProperty('--holiday-grid-copy-offset', `${copyOffset}px`);
      this.classList.add('is-scroll-ready');
      this.update();
    }

    resetScroll() {
      this.classList.remove('is-scroll-ready', 'is-scroll-animating');
      this.cards.forEach((card) => card.style.removeProperty('--holiday-grid-rotation'));
      this.progress = null;
    }

    scheduleUpdate() {
      if (this.scrollFrame || !this.classList.contains('is-scroll-ready')) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.update();
      });
    }

    update() {
      if (!this.classList.contains('is-scroll-ready')) return;
      const top = this.track.getBoundingClientRect().top;
      const progress = Math.max(0, Math.min(1, (this.pinTop - top) / this.scrollDistance));
      if (progress === this.progress) return;
      this.progress = progress;
      this.classList.toggle('is-scroll-animating', progress > 0 && progress < 1);
      if (progress > 0) this.classList.add('has-interacted');

      const stagger = 0.055;
      const flipDuration = 1 - stagger * (this.cards.length - 1);
      this.cards.forEach((card, index) => {
        const cardProgress = Math.max(0, Math.min(1, (progress - index * stagger) / flipDuration));
        card.style.setProperty('--holiday-grid-rotation', `${cardProgress * 180}deg`);
        this.setCardState(card, cardProgress >= 0.5);
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.measureFrame);
      cancelAnimationFrame(this.scrollFrame);
      this.measureFrame = null;
      this.scrollFrame = null;
    }
  });
}
